'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';
import { useRouter } from 'next/navigation';

interface EmployeeProfile {
  id: string;
  authUserId: string;
  username: string;
  fullName: string;
  email: string;
  role: 'administrator' | 'sales_rep' | 'field_operator';
  accountStatus: 'pending' | 'approved' | 'rejected';
  avatarUrl?: string;
  phone?: string;
  territory?: string;
  commissionRate: number;
  createdAt: string;
}

interface PortalAuthContextType {
  user: any;
  employee: EmployeeProfile | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (data: { username: string; fullName: string; email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
  refreshEmployee: () => Promise<void>;
  signOutMessage: string | null;
  clearSignOutMessage: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextType>({} as PortalAuthContextType);

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return ctx;
};

export const PortalAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [employee, setEmployee] = useState<EmployeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [signOutMessage, setSignOutMessage] = useState<string | null>(null);
  const supabase = createClient();
  const router = useRouter();

  const fetchEmployee = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('employee_profiles')
        .select('*')
        .eq('auth_user_id', userId)
        .single();
      if (error || !data) { setEmployee(null); return; }
      setEmployee({
        id: data.id,
        authUserId: data.auth_user_id,
        username: data.username,
        fullName: data.full_name,
        email: data.email,
        role: data.role,
        accountStatus: data.account_status,
        avatarUrl: data.avatar_url,
        phone: data.phone,
        territory: data.territory,
        commissionRate: data.commission_rate,
        createdAt: data.created_at,
      });
    } catch { setEmployee(null); }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchEmployee(session.user.id).finally(() => setLoading(false));
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchEmployee(session.user.id);
      else { setEmployee(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (username: string, password: string) => {
    // Use SECURITY DEFINER RPC function to bypass RLS for pre-auth username lookup
    // Direct table query fails because RLS blocks anon reads on employee_profiles
    const { data: profileData, error: profileError } = await supabase
      .rpc('get_employee_login_info', { p_username: username });

    if (profileError || !profileData || profileData.length === 0) {
      throw new Error('Invalid username or password');
    }

    const profile = profileData[0];

    if (profile.account_status === 'pending') throw new Error('Your account is pending approval by an administrator.');
    if (profile.account_status === 'rejected') throw new Error('Your account has been rejected. Please contact management.');

    const { error } = await supabase.auth.signInWithPassword({ email: profile.email, password });
    if (error) throw new Error('Invalid username or password');

    // Redirect admins to admin dashboard, others to regular dashboard
    if (profile.role === 'administrator') {
      router.push('/portal/admin-dashboard');
    } else {
      router.push('/portal/dashboard');
    }
  };

  const signUp = async (data: { username: string; fullName: string; email: string; password: string }) => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: { data: { full_name: data.fullName, username: data.username } }
    });
    if (authError) throw authError;
    if (!authData.user) throw new Error('Signup failed');

    const { error: profileError } = await supabase.from('employee_profiles').insert({
      auth_user_id: authData.user.id,
      username: data.username,
      full_name: data.fullName,
      email: data.email,
      role: 'sales_rep',
      account_status: 'pending',
    });
    if (profileError) throw profileError;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setEmployee(null);
    // Store success message in sessionStorage so login page can display it
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('signOutSuccess', 'You have been signed out successfully.');
      window.location.href = 'https://riograndeps.com';
    }
  };

  const clearSignOutMessage = () => setSignOutMessage(null);

  const refreshEmployee = async () => {
    if (user) await fetchEmployee(user.id);
  };

  return (
    <PortalAuthContext.Provider value={{ user, employee, loading, signIn, signUp, signOut, refreshEmployee, signOutMessage, clearSignOutMessage }}>
      {children}
    </PortalAuthContext.Provider>
  );
};
